// Libs
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom';
// Global Context
import { useAlert } from '../AlertContext';
import { useUser } from '../context/UserContext';
// Hooks
import { useShowAlert } from "../hooks/useShowAlert";
import useMemberRedirect from '../hooks/useMemberRedirect';
// Api Requests
import { getBookingsByUserId, deleteMemberBooking } from "../api/Bookings";
// Types
import { Booking } from "../types/types";
// Components
import Loading from "../components/Alerts/Loading";
import AlertMessage from '../components/Alerts/AlertMessage';
import MemberLandingList from "../components/Events/MemberLandingList";
import ActiveBookingsButton from "../components/Bookings/ActiveBookingsButton";
import ErrorPage from './ErrorPage';
// Material-UI
import { Box, Button, Card, CardContent, Container, Typography } from '@mui/material';
// Material-UI Icons
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

export default function UserLandingPage() {
  const { alert } = useAlert();
  const showAlert = useShowAlert();
  const navigate = useNavigate();
  const { user, isUserLoading } = useUser();

  // Redirect user if not a member user
  useMemberRedirect(user, isUserLoading);

  const sendCancelRequest = async (userId: string, bookedUserId: string, eventId: string, clubName: string) => {
    try {
      const response = await deleteMemberBooking(userId, bookedUserId, eventId, clubName);
      
      if (response.success) {
        const { message, subMessage } = response.success;
        showAlert(message, subMessage, 'success');
        queryClient.invalidateQueries({ queryKey: ['memberBookings', userId, user?.timezone] });
      }

      if (response.error) {
        const { message, subMessage } = response.error;
        showAlert(message, subMessage, 'error');
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      showAlert('An error has occurred', 'Please Try Again', 'error');
    }
  }

  // Retrieve existing bookings for member
  const queryClient = useQueryClient();
  const { data: bookingsData, error: existingBookingsError, isLoading: isBookingsLoading } = useQuery({
    queryKey: ['memberBookings', user?.id, user?.timezone],
    queryFn: () => getBookingsByUserId(user?.id ?? '', user?.timezone ?? ''),
    enabled: !!user?.id && !!user?.timezone,
  });

  // If still loading, show loading state
  if (isUserLoading || isBookingsLoading) {
    return <Loading text="" />
  }

  if (existingBookingsError) {
    <ErrorPage
      title="Oops! Something went wrong"
      message="We're sorry, but we encountered an unexpected error while processing your request"
    />
  }

  return (
    <Container component="main" maxWidth="sm" sx={{ overflow: 'auto', paddingBottom: '100px', pt: '64px' }}>
      {alert && (
        <AlertMessage />
      )}
      <Box textAlign={'center'} sx={{marginTop: '5px'}}>
        <Typography variant='h6' sx={{ paddingTop: 2, paddingBottom: 1 }}>Hi, {user?.first_name}</Typography>
      </Box>

      {/* Active Bookings Banner */}
      <ActiveBookingsButton bookingsData={bookingsData}/>
        
      {/* Book A Class Button */}
      {bookingsData?.length === 0 && (
      <Box textAlign={'center'} sx={{marginTop: '15px'}}>
        <Card >
          <CardContent>
            <Box>
              <CalendarMonthIcon fontSize="large"/>
            </Box>
            <Button variant="contained" onClick={() => navigate('/bookclasses')}>Book A Class Now</Button>
          </CardContent>
        </Card>
      </Box>
      )}

      {/* Active Bookings */}
      {(bookingsData?.length ?? 0) > 0 && bookingsData && bookingsData.map(([date, classItems]: [string, Booking[]]) => (
        <MemberLandingList key={date} date={date} classItems={classItems} user={user} navigate={navigate} sendCancelRequest={sendCancelRequest} />
      ))}

    </Container>
  );
};
